import { useContext, useState } from 'react';
import { MapContext } from '../../../context/MapContext';
import { bgLayers, bgMapObject, householdLayers, poiLayers } from '../layer';
import './Legend.css';
import { poiList } from '../../../common/data/list';
import { IconButton, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

const popLegendColor = [
	{
		text: '20 미만',
		color: '#F7FBFF',
	},
	{
		text: '20 이상 ~ 100 미만',
		color: '#DEEAF7',
	},
	{
		text: '100 이상 ~ 200 미만',
		color: '#97C6E0',
	},
	{
		text: '200 이상 ~ 500 미만',
		color: '#67A9D3',
	},
	{
		text: '500 이상 ~ 1,000 미만',
		color: '#2267AB',
	},
	{
		text: '1,000 이상',
		color: '#193F75',
	},
];

const boundaryMapStyleObject = {
	basic: 'flow_sido_boundary_black',
	white: 'flow_sido_boundary_black',
	satellite: 'flow_sido_boundary_white',
};

export default function MapLegend() {
	const {
		collapseBgMap,
		setCollapseBgMap,
		bgMap,
		setBgMap,
		emdBoundary,
		setEmdBoundary,
		aggGrid,
		setAggGrid,
		collapsePopGrid,
		setCollapsePopGrid,
		poiInfo,
		setPoiInfo,
	} = useContext(MapContext);

	const [mapLegendOpen, setMapLegendOpen] = useState(true);
	const [sphGrid, setSphGrid] = useState(true);

	const match = {
		aggGrid: {
			isChecked: setAggGrid,
			layer: householdLayers.sphAggGridLayer,
		},
		popGrid: {
			isChecked: setCollapsePopGrid,
			layer: householdLayers.sphPopGridLayer,
		},
		sphGrid: {
			isChecked: setSphGrid,
			layer: householdLayers.sphGridLayer
		}
	};

	const handleOption = (e) => {
		const value = e.target.value;
		match[value].isChecked(e.target.checked);
		match[value].layer.setVisible(e.target.checked);
	};

	const handlePoi = async (e) => {
		const value = e.target.value;

		let nextValue;
		if (value === 'poi') {
			if (e.target.checked) {
				nextValue = [
					'admCenter',
					'university',
					'industry',
					'hospital',
					'security',
				];
			} else {
				nextValue = [];
			}

			poiList.forEach((layer) => {
				layer.layerName.setVisible(e.target.checked);
			});
			poiLayers.industryPolygon.setVisible(e.target.checked);
		} else {
			if (e.target.checked) {
				nextValue = [...poiInfo, value];
			} else {
				nextValue = [...poiInfo.filter((code) => code !== value)];
			}

			poiList.forEach((layer) => {
				if (value === layer.value) {
					layer.layerName.setVisible(e.target.checked);
				}
			});

			if (value === 'industry') {
				poiLayers.industryPolygon.setVisible(e.target.checked);
			}
		}
		setPoiInfo(nextValue);
	};

	const handleEmd = (e) => {
		const value = e.target.value;
		if (value === 'emd') {
			setEmdBoundary(e.target.checked);
			householdLayers.bgEmdLayer.setVisible(e.target.checked);
		}
	};

	const handleCollapseBgMap = (e) => {
		const value = e.target.value;
		if (value === 'collapseBgMap') {
			setCollapseBgMap(e.target.checked);
		}
	};

	const handleBgMap = (e) => {
		const value = e.target.value;
		setBgMap(value);
		Object.values(bgMapObject).forEach((name) =>
			bgLayers[name].setVisible(!e.target.checked)
		);

		['bgEmdLayer', 'bgSggLayer', 'bgSidoLayer'].forEach((target) =>
			householdLayers[target].getSource().updateParams({
				styles: boundaryMapStyleObject[value],
			})
		);

		if (value !== 'none') {
			bgLayers[bgMapObject[value]].setVisible(e.target.checked);
		}
	};

	return (
		<div className="mapOptionModal">
			<div className="header">
				<h1>현황 보기</h1>
				<button onClick={() => setMapLegendOpen(!mapLegendOpen)}>
					<img
						src="img/arrowIcon.png"
						alt="탭 활성화"
						className={mapLegendOpen ? 'closeArrow' : 'closeArrow hide'}
					/>
				</button>
			</div>
			<div className={mapLegendOpen ? 'modalBody' : 'modalBody hide'}>
				<label htmlFor="" className="chk_box tit">
					<input
						type="checkbox"
						name=""
						id=""
						value="aggGrid"
						onChange={handleOption}
						checked={aggGrid}
					/>
					<span className='orangeBox'></span>
					1인가구 집적지
					<Tooltip
						title={
							<Typography color="inherit" className='tooltipText'>
								<span>'1인가구 집적지'란 다음 두 기준 중 하나를 충족하는 격자를 의미합니다.</span>
								<li>기준 1: 1㎢ 내 1인가구 수가 100가구 이상이면서 전체 가구 중 1인가구 비중이 50% 이상인 곳</li>
								<li>기준 2: 1㎢ 내 1인가구 수가 2500가구 이상인 곳</li>
							</Typography>
						}
						arrow
						placement="right"
					>
						<IconButton size="small" className="infoTooltipBtn">
							<InfoIcon className="infoTooltipIcon"></InfoIcon>
						</IconButton>
					</Tooltip>
				</label>
				<label htmlFor="" className="chk_box tit">
					<input
						type="checkbox"
						name=""
						id=""
						value="popGrid"
						onChange={handleOption}
						checked={collapsePopGrid}
					/>
					1인가구 수 밀도
				</label>
				{collapsePopGrid ? (
					<div>
						{popLegendColor.map((obj, i) => {
							return (
								<label key={i} className="chk_box depth3">
									<span
										className="LegendColorBox"
										style={{ backgroundColor: obj.color }}
									></span>
									<p className="LegendColorText">{obj.text}</p>
								</label>
							);
						})}
					</div>
				) : null}
				<label htmlFor="" className="chk_box tit">
					<input
						type="checkbox"
						name=""
						id=""
						value="sphGrid"
						onChange={handleOption}
						checked={sphGrid}
					/>
					<span className="pointcolor yell"></span>
					인구 거주 격자
				</label>
				<label htmlFor="facilityTotal" className="chk_box tit">
					<input
						type="checkbox"
						value="poi"
						checked={poiInfo.length === 5}
						onChange={handlePoi}
					/>
					주요시설
				</label>
				<label htmlFor="" className="chk_box depth2">
					<input
						type="checkbox"
						value="admCenter"
						checked={poiInfo.includes('admCenter')}
						onChange={handlePoi}
					/>
					<img src="img/AdmCenter.svg" alt={'AdmCenter'} />
					행정복지센터<span style={{ fontSize: '1rem' }}>(동·읍·면)</span>
				</label>
				<label htmlFor="" className="chk_box depth2">
					<input
						type="checkbox"
						value="university"
						checked={poiInfo.includes('university')}
						onChange={handlePoi}
					/>
					<img src="img/University.svg" alt={'University'} />
					대학교
				</label>
				<label htmlFor="" className="chk_box depth2">
					<input
						type="checkbox"
						value="industry"
						checked={poiInfo.includes('industry')}
						onChange={handlePoi}
					/>
					<img src="img/Industry.svg" alt={'Industry'} />
					산업단지
				</label>
				<label htmlFor="" className="chk_box depth2">
					<input
						type="checkbox"
						value="hospital"
						checked={poiInfo.includes('hospital')}
						onChange={handlePoi}
					/>
					<img src={`img/Hospital.svg`} alt={'Hospital'} />
					병·의원
				</label>
				<label htmlFor="" className="chk_box depth2">
					<input
						type="checkbox"
						value="security"
						checked={poiInfo.includes('security')}
						onChange={handlePoi}
					/>
					<img src={`img/Security.svg`} alt={'Security'} />
					경찰서<span style={{ fontSize: '1rem' }}>(지구대, 파출소 포함)</span>
				</label>
				<span className="line"></span>
				<label htmlFor="facility" className="chk_box tit">
					<input
						type="checkbox"
						value="collapseBgMap"
						defaultChecked
						onChange={handleCollapseBgMap}
					/>
					배경지도
				</label>
				{collapseBgMap ? (
					<div className="depth">
						<label htmlFor="radio2" className="rad_box depth2">
							<input
								type="radio"
								value="basic"
								checked={bgMap === 'basic'}
								onChange={handleBgMap}
							/>
							일반
						</label>
						<label htmlFor="radio3" className="rad_box depth2">
							<input
								type="radio"
								value="white"
								checked={bgMap === 'white'}
								onChange={handleBgMap}
							/>
							백지도
						</label>
						<label htmlFor="radio4" className="rad_box depth2">
							<input
								type="radio"
								value="satellite"
								checked={bgMap === 'satellite'}
								onChange={handleBgMap}
							/>
							위성
						</label>
					</div>
				) : null}
				<label htmlFor="facility3" className="chk_box tit">
					<input
						type="checkbox"
						name=""
						id="facility3"
						value="emd"
						checked={emdBoundary}
						onChange={handleEmd}
					/>
					읍·면·동 경계
				</label>
			</div>
		</div>
	);
}
